import { useCallback, useContext, useMemo } from 'react';

import { FlowPermissions, Group, User } from 'vivaa-api';

import { AuthContext } from './AuthContext.js';
import { EntityPermission } from './permissions.js';
import { isDefined, isTruthy } from '../utils/types.js';

export function useAuthContext() {
  return useContext(AuthContext);
}

const { Permission } = Group;

export function useLoggedInAuthContext() {
  const loggedInContext = useAuthContext().getLoggedIn();
  const { user, permissions, allUserRebacPermissions } = loggedInContext;
  const isAdminRole = user.role === User.Role.Admin;
  const isStaffRole = user.role === User.Role.Staff;
  const isAnalystRole = user.role === User.Role.Analyst;
  const defaultOrganization = user.organizations.length === 1 ? user.organizations[0] : undefined;
  const hasMultipleOrganizations = user.organizations.length > 1;
  const organizationsAreVisible = isAdminRole || hasMultipleOrganizations;

  const hasPermission = useMemo(() => {
    const output = {} as Record<keyof typeof Permission, true | undefined>;
    for (const [key, value] of Object.entries(Permission)) {
      if (permissions.includes(value)) {
        output[key as keyof typeof Permission] = true;
      }
    }
    return output;
  }, [permissions]);

  const hasAnyAgentAccess = useMemo(() => {
    const hasAnyRebacPermissions = Object.keys(allUserRebacPermissions).length > 0;
    return isTruthy(hasPermission.ViewAgents) || hasAnyRebacPermissions;
  }, [hasPermission, allUserRebacPermissions]);

  const canModifyOrgAgents = useMemo(() => {
    if (hasPermission.ModifyAgents) {
      return true;
    }

    const orgPermissions =
      allUserRebacPermissions[FlowPermissions.PermissionEntityType.Organization] ?? {};
    return Object.values(orgPermissions).some((perms) =>
      perms.includes(FlowPermissions.FlowRebacPermission.AgentModify)
    );
  }, [hasPermission, allUserRebacPermissions]);

  const hasAnyAgentGrantPermission = useMemo(() => {
    if (hasPermission.ManageGlobalFlowTags) {
      return true;
    }

    return Object.values(allUserRebacPermissions).some((entityPerms) =>
      Object.values(entityPerms ?? {}).some((perms) =>
        perms.includes(FlowPermissions.FlowRebacPermission.AgentGrant)
      )
    );
  }, [hasPermission.ManageGlobalFlowTags, allUserRebacPermissions]);

  const checkAgentPermissions = useCallback(
    ({
      agentId,
      organizationId,
    }: {
      agentId?: string | null | undefined;
      organizationId?: string;
    }): Record<EntityPermission, boolean> => {
      const hasRebacPermission = (permission: FlowPermissions.FlowRebacPermission): boolean => {
        if (isDefined(agentId)) {
          const agentPermissions =
            allUserRebacPermissions[FlowPermissions.PermissionEntityType.Agent]?.[agentId] ?? [];
          if (agentPermissions.includes(permission)) {
            return true;
          }
        }

        if (isDefined(organizationId)) {
          const orgPermissions =
            allUserRebacPermissions[FlowPermissions.PermissionEntityType.Organization]?.[
              organizationId
            ] ?? [];
          if (orgPermissions.includes(permission)) {
            return true;
          }
        }

        return false;
      };

      return {
        [EntityPermission.View]:
          isTruthy(hasPermission.ViewAgents) ||
          hasRebacPermission(FlowPermissions.FlowRebacPermission.AgentView),
        [EntityPermission.Modify]:
          isTruthy(hasPermission.ModifyAgents) ||
          hasRebacPermission(FlowPermissions.FlowRebacPermission.AgentModify),
        [EntityPermission.Publish]:
          isTruthy(hasPermission.ModifyAgentsPublish) ||
          hasRebacPermission(FlowPermissions.FlowRebacPermission.AgentPublish),
        [EntityPermission.Grant]: hasRebacPermission(
          FlowPermissions.FlowRebacPermission.AgentGrant
        ),
      };
    },
    [hasPermission, allUserRebacPermissions]
  );

  return {
    isAdminRole,
    isStaffRole,
    isAnalystRole,
    defaultOrganization,
    hasMultipleOrganizations,
    organizationsAreVisible,
    hasPermission,
    hasViewAgentOrFlowBuilderAncillaryPermissions:
      isTruthy(hasPermission.ViewAgents) || isTruthy(hasPermission.ViewFlowBuilderAncillary),
    hasModifyAgentOrFlowBuilderAncillaryPermissions:
      isTruthy(hasPermission.ModifyAgents) || isTruthy(hasPermission.ModifyFlowBuilderAncillary),
    hasAnyAgentAccess,
    canModifyOrgAgents,
    hasAnyAgentGrantPermission,
    checkAgentPermissions,
    ...loggedInContext,
  };
}
